import type {
  CampaignListItem,
  CampaignListItemServer,
  CampaignsFilter,
} from '@metapic-apps/shared'
import { type MaybeRefOrGetter, type Ref, ref, toValue, watch } from 'vue'

import {
  makeQueryParamsForCampaigns,
  normalizeCampaignListItem,
} from '@/lib/adapters/campaigns.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import { useErrorStore } from '@/stores/error'
import { useUserStore } from '@/stores/user'

export const useGetCampaigns = (
  filterRef: Ref<CampaignsFilter>,
  options?: {
    linkRef?: Ref<string | undefined>
    skip?: MaybeRefOrGetter<boolean>
  },
) => {
  const { linkRef, skip = false } = options ?? {}
  const data = ref<CampaignListItem[] | undefined>()
  const totalPages = ref<number>(0)
  const links = ref<{ first?: string; last?: string; prev?: string; next?: string }>({})
  const userStore = useUserStore()
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchCampaigns = async (params?: URLSearchParams, directLink?: string) => {
    try {
      data.value = undefined
      isLoading.value = true
      error.value = null

      const response = directLink
        ? await request.get(directLink)
        : await request.get('/v2/offers', { params })
      data.value = response.data.data.map((item: CampaignListItemServer) =>
        normalizeCampaignListItem(item),
      )
      totalPages.value = response.data.meta.last_page
      links.value = response.data.links
    } catch (e) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      isLoading.value = false
    } finally {
      isLoading.value = false
    }
  }

  watch(
    [filterRef, () => toValue(skip)],
    async () => {
      if (!toValue(skip)) {
        const filter = toValue(filterRef)
        const params = makeQueryParamsForCampaigns(filter, userStore.id)

        if (params) {
          await fetchCampaigns(params)
        }
      }
    },
    { immediate: true, deep: true },
  )

  watch(
    () => linkRef?.value,
    async () => {
      if (!toValue(skip)) {
        const link = toValue(linkRef)
        if (link) {
          await fetchCampaigns(undefined, link)
        }
      }
    },
  )

  return {
    data,
    links,
    totalPages,
    isLoading,
    error,
  }
}
